/**
 * Files and photos travel as base64 inside one JSON body the API caps at 25 MB
 * (express.json in api/src/index.js), and base64 is a third bigger than the
 * file. 18 MB of files is 24 MB of base64, which leaves room for the rest of
 * the form, so 18 MB is what one save can carry. Engineers' phone photos are
 * 3-5 MB each and nothing downscales them: four 4 MB photos fit, five do not.
 *
 * The cap was 10 MB until 2026-09-21, and the picker checked 8 MB a file and
 * never the total. One 7.9 MB photo, or three 3 MB photos, came back as
 * "Internal server error" with nothing saved (team leader scheduling work).
 * request-body-too-large.test.js in the API proves the full 18 MB still fits.
 * Bulletins keep their own, smaller budget: NEW_FILES_BUDGET_BYTES in
 * utils/bulletins.js.
 */
export const ONE_SAVE_BUDGET_BYTES = 18 * 1024 * 1024;
/**
 * The most one file can be. The API holds each file to the same figure
 * (MAX_ATTACHMENT_BYTES in api/src/utils/attachments.js), and would refuse the
 * save with less helpful words.
 */
export const MAX_FILE_BYTES = 8 * 1024 * 1024;
const wholeMegabytes = (bytes) => `${bytes / (1024 * 1024)} MB`;
const BUDGET_LABEL = wholeMegabytes(ONE_SAVE_BUDGET_BYTES);
const FILE_LIMIT_LABEL = wholeMegabytes(MAX_FILE_BYTES);
const ALLOWED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain'
]);

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.txt'];

const megabytes = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

export function validateAttachment(file) {
  if (!file) return null;

  if (file.size > MAX_FILE_BYTES) {
    return `"${file.name}" is too big (${megabytes(file.size)}). A file can be up to ${FILE_LIMIT_LABEL}.`;
  }

  if (file.type && !ALLOWED_TYPES.has(file.type)) {
    return `"${file.name}" has unsupported type "${file.type}". Allowed: images, PDF, text files`;
  }

  const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
  if (ext && !ALLOWED_EXTENSIONS.includes(ext) && !ALLOWED_TYPES.has(file.type)) {
    return `"${file.name}" has unsupported extension. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
  }

  return null;
}

export function validateAttachments(files) {
  const selectedFiles = Array.from(files || []);
  const errors = [];
  for (const file of selectedFiles) {
    const error = validateAttachment(file);
    if (error) errors.push(error);
  }

  // Each file fits, but together they are more than one save can carry. A file
  // too big on its own has already been named above, which says it better.
  const anyTooBig = selectedFiles.some(file => file && file.size > MAX_FILE_BYTES);
  const totalBytes = selectedFiles.reduce((total, file) => total + (Number(file && file.size) || 0), 0);
  if (!anyTooBig && totalBytes > ONE_SAVE_BUDGET_BYTES) {
    errors.push(`These files add up to ${megabytes(totalBytes)}, more than the ${BUDGET_LABEL} that can be saved in one go. `
      + 'Attach fewer or smaller files.');
  }

  return errors;
}

export function filesToAttachmentPayload(files) {
  const selectedFiles = Array.from(files || []);

  const validationErrors = validateAttachments(selectedFiles);
  if (validationErrors.length > 0) {
    return Promise.reject(new Error(validationErrors.join('; ')));
  }

  return Promise.all(selectedFiles.map(file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      name: file.name,
      size: file.size,
      type: file.type,
      dataUrl: reader.result
    });
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  })));
}

export function normaliseAttachmentList(attachments = []) {
  return Array.isArray(attachments) ? attachments : [];
}

/**
 * What stays attached when a pick is refused on a picker that replaces its
 * selection: the files already saved, none of the unsaved ones. The browser
 * has already dropped the earlier pick from the picker, and the page empties
 * it, so nothing the picker no longer shows is sent with the save.
 */
export function savedAttachmentsOnly(attachments) {
  return normaliseAttachmentList(attachments).filter(attachment => attachment && !attachment.dataUrl);
}
